/**
 * Locale context for the SPA. Mounted ABOVE the router (LoginPage is public and
 * localized too). Exposes the active typed catalog as `t` plus locale-bound
 * Intl formatters; switching locale re-renders the tree, persists the choice,
 * and keeps `<html lang>` in sync for a11y / spellcheck / hyphenation.
 */

import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import * as fmt from './format'
import { detectLocale, type Locale, persistLocale } from './locale'
import { catalogs, type Messages } from './messages'

export interface I18n {
  locale: Locale
  /**
   * Switch the UI language. Persisted by default (the reader chose it); a page that picks a
   * language FOR the reader — the rating page opening in the answer's language — passes
   * `{ persist: false }` so the pick never outlives the page.
   */
  setLocale: (locale: Locale, options?: { persist?: boolean }) => void
  t: Messages
  formatDate: (value: Date | string | number) => string
  formatDateTime: (value: Date | string | number) => string
  formatTime: (value: Date | string | number) => string
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string
  formatBytes: (bytes: number) => string
  formatAgo: (value: Date | string | number, nowMs?: number) => string
  formatDuration: (ms: number | null | undefined) => string
}

const I18nContext = createContext<I18n | null>(null)

export function I18nProvider({ children, initialLocale }: { children: ReactNode; initialLocale?: Locale }) {
  const [locale, setLocaleState] = useState<Locale>(() => initialLocale ?? detectLocale())

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  const setLocale = useCallback((next: Locale, options?: { persist?: boolean }) => {
    setLocaleState(next)
    if (options?.persist !== false) persistLocale(next)
  }, [])

  const value = useMemo<I18n>(
    () => ({
      locale,
      setLocale,
      t: catalogs[locale],
      formatDate: (v) => fmt.formatDate(locale, v),
      formatDateTime: (v) => fmt.formatDateTime(locale, v),
      formatTime: (v) => fmt.formatTime(locale, v),
      formatNumber: (v, options) => fmt.formatNumber(locale, v, options),
      formatBytes: (v) => fmt.formatBytes(locale, v),
      formatAgo: (v, nowMs) => fmt.formatAgo(locale, v, nowMs),
      formatDuration: (ms) => fmt.formatDuration(locale, ms)
    }),
    [locale, setLocale]
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18n {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n must be used within I18nProvider')
  return ctx
}
